import type { ComponentProps, ReactNode } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import RadioGroupItem from "./RadioGroupItem"

export type RadioGroupChoiceCardProps = Omit<
  ComponentProps<typeof RadioGroupItem>,
  "children" | "title"
> & {
  /** The item's id: the card is a <label htmlFor> around the radio. */
  id: string
  title: ReactNode
  description?: ReactNode
  className?: string
}

/**
 * A label-as-card around one RadioGroup.Item: clicking anywhere on the card
 * selects it, the checked state paints the border via `has-[]` on the radio's
 * data-state. Must be rendered inside a RadioGroup.
 */
function RadioGroupChoiceCard(props: RadioGroupChoiceCardProps) {
  const { id, title, description, className, ...itemProps } = props
  return (
    <label
      htmlFor={id}
      className={cn(
        "flex cursor-pointer items-start gap-3 rounded-md border border-input p-3 text-sm transition-colors",
        "hover:bg-accent/50 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-accent/30",
        "has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50",
        className,
      )}
    >
      <RadioGroupItem id={id} className="mt-0.5" {...itemProps} />
      <span className="flex flex-col gap-1">
        <span className="font-medium leading-none">{title}</span>
        {description ? (
          <span className="text-xs text-muted-foreground">{description}</span>
        ) : null}
      </span>
    </label>
  )
}

export default RadioGroupChoiceCard
